/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { DocumentsListTableData } from '../../components/RegistryWidgets/CashRegisters/CashRegisterCard/CashRegisterTabs/DocumentsList/DocumentsListConfig';
import {
    CashRegisterModelDetailsDto,
    VersionWithCheckSums,
    SoftwareVersionsHistory,
    ModelDocumentsListDto,
    FactoryNumberTypes,
    FactoryNumbersListDto,
    FactoryNumbersListFilterParams,
} from '../api/cashRegisters/cashRegistersClient.typings';
import { CashRegistersDetailsAction, CashRegistersDetailsActionType } from '../actions/cashRegistersDetailsActions';
import { Order, PageQueryParams, SortQueryParamsGeneric } from '../api/apiCommon.typings';

export const docsRowsPerPage = [20, 30, 40, 50];
export const defaultDocsPaginationLimit = docsRowsPerPage[0];
export const defaultDocsSortingKey: keyof DocumentsListTableData = 'name';
export const defaultDocsSortingOrder: Order = 'asc';

export const versionsRowsPerPage = [15, 20, 25];
export const defaultVersionsPaginationLimit = versionsRowsPerPage[0];

export const checksumsRowsPerPage = [5, 10, 15];
export const defaultChecksumsPaginationLimit = checksumsRowsPerPage[0];

export const factoryNumbersRowsPerPage = [15, 20, 25, 30];
export const defaultFactoryNumbersPaginationLimit = factoryNumbersRowsPerPage[0];
export const defaultFactoryNumber = '';
export const defaultFactoryNumberType: FactoryNumberTypes = 'ALL';

export interface CashRegisterDetailsState {
    cashRegisterTaxNumber: string;
    cashRegisterModel: CashRegisterModelDetailsDto;
    versionsHistory: SoftwareVersionsHistory;
    versionsPageParams: PageQueryParams;
    documentsList: ModelDocumentsListDto;
    docsSortParams: SortQueryParamsGeneric<keyof DocumentsListTableData>;
    docsPageParams: PageQueryParams;
    factoryNumbersList: FactoryNumbersListDto;
    factoryNumbersListPageParams: PageQueryParams;
    factoryNumbersListFilterParams: FactoryNumbersListFilterParams;
    isLoading: boolean;
    isLoadingStartAttestation: boolean;
    isLoadingCompleteAttestation: boolean;
    isLoadingVersionsHistory: boolean;
    isLoadingDocumentsList: boolean;
    isLoadingFactoryNumbersList: boolean;
    currentInfoTab: number;
}

const initialState: CashRegisterDetailsState = {
    cashRegisterTaxNumber: null,
    cashRegisterModel: null,
    versionsHistory: null,
    versionsPageParams: {
        limit: defaultVersionsPaginationLimit,
        offset: 0,
    },
    documentsList: null,
    docsPageParams: {
        limit: defaultDocsPaginationLimit,
        offset: 0,
    },
    docsSortParams: {
        sortType: defaultDocsSortingKey,
        sortOrder: defaultDocsSortingOrder,
    },
    factoryNumbersList: null,
    factoryNumbersListPageParams: {
        limit: defaultFactoryNumbersPaginationLimit,
        offset: 0,
    },
    factoryNumbersListFilterParams: {
        factoryNumber: defaultFactoryNumber,
        used: defaultFactoryNumberType,
    },
    isLoading: false,
    isLoadingStartAttestation: false,
    isLoadingCompleteAttestation: false,
    isLoadingVersionsHistory: false,
    isLoadingDocumentsList: false,
    isLoadingFactoryNumbersList: false,
    currentInfoTab: 0,
};

export function cashRegisterDetailsReducer(
    state: CashRegisterDetailsState = initialState,
    action: CashRegistersDetailsAction,
): CashRegisterDetailsState {
    switch (action.type) {
        case CashRegistersDetailsActionType.OPEN_REGISTRY_CASH_REGISTER_CARD_PAGE:
            const previousTaxNumber = state.cashRegisterTaxNumber;
            const currentTaxNumber = action.payload;

            let isNewCardOpen = true;
            if (previousTaxNumber && currentTaxNumber) isNewCardOpen = previousTaxNumber !== currentTaxNumber;
            return {
                ...state,
                isLoading: true,
                cashRegisterTaxNumber: action.payload,
                docsPageParams: initialState.docsPageParams,
                docsSortParams: initialState.docsSortParams,
                documentsList: initialState.documentsList,
                versionsHistory: initialState.versionsHistory,
                versionsPageParams: initialState.versionsPageParams,
                factoryNumbersList: initialState.factoryNumbersList,
                factoryNumbersListPageParams: isNewCardOpen
                    ? initialState.factoryNumbersListPageParams
                    : { ...state.factoryNumbersListPageParams },
                factoryNumbersListFilterParams: isNewCardOpen
                    ? initialState.factoryNumbersListFilterParams
                    : { ...state.factoryNumbersListFilterParams },
            };

        case CashRegistersDetailsActionType.FETCH_CASH_REGISTER_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                cashRegisterModel: action.payload,
            };

        case CashRegistersDetailsActionType.FETCH_MODEL_DOCUMENTS_START:
            return {
                ...state,
                isLoadingDocumentsList: true,
            };

        case CashRegistersDetailsActionType.FETCH_MODEL_DOCUMENTS_SUCCESS:
            return {
                ...state,
                documentsList: action.payload,
                isLoadingDocumentsList: false,
            };

        case CashRegistersDetailsActionType.SUBMIT_DOCUMENTS_SUCCESS:
            return {
                ...state,
                isLoadingDocumentsList: true,
            };

        case CashRegistersDetailsActionType.DELETE_DOCUMENTS_SUCCESS:
        case CashRegistersDetailsActionType.DELETE_DOCUMENTS_ERROR: //some files might be deleted
            return {
                ...state,
                isLoadingDocumentsList: true,
            };

        case CashRegistersDetailsActionType.APPLY_DOCUMENTS_SORTING:
            return {
                ...state,
                docsSortParams: action.payload,
                isLoadingDocumentsList: true,
            };

        case CashRegistersDetailsActionType.APPLY_DOCUMENTS_PAGING:
            return {
                ...state,
                docsPageParams: action.payload,
                isLoadingDocumentsList: true,
            };

        case CashRegistersDetailsActionType.UPDATE_CASH_REGISTER_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: true, //for details data reloading
            };

        case CashRegistersDetailsActionType.START_ATTESTATION:
            return {
                ...state,
                isLoadingStartAttestation: true,
            };

        case CashRegistersDetailsActionType.START_ATTESTATION_SUCCESS:
            return {
                ...state,
                isLoadingStartAttestation: false,
                isLoading: true, //for details data reloading
                isLoadingVersionsHistory: true, //for versions list reloading
                isLoadingDocumentsList: true, //for documents list reloading, docs can be added
                versionsPageParams: initialState.versionsPageParams,
            };

        case CashRegistersDetailsActionType.COMPLETE_ATTESTATION:
            return {
                ...state,
                isLoadingCompleteAttestation: true,
            };

        case CashRegistersDetailsActionType.COMPLETE_ATTESTATION_SUCCESS:
            return {
                ...state,
                isLoadingCompleteAttestation: false,
                isLoading: true, //for details data reloading
                isLoadingVersionsHistory: true, //for versions list reloading
                versionsPageParams: initialState.versionsPageParams,
            };

        case CashRegistersDetailsActionType.FETCH_SOFTWARE_VERSIONS_PAGE_START:
        case CashRegistersDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START:
            return {
                ...state,
                isLoadingVersionsHistory: true,
            };

        case CashRegistersDetailsActionType.FETCH_SOFTWARE_VERSIONS_SUCCESS:
            return {
                ...state,
                versionsHistory: action.payload,
                isLoadingVersionsHistory: false,
            };

        case CashRegistersDetailsActionType.APPLY_VERSIONS_PAGING:
            return {
                ...state,
                versionsPageParams: action.payload,
            };

        case CashRegistersDetailsActionType.FETCH_VERSION_CHECK_SUMS_START:
            return {
                ...state,
                isLoadingVersionsHistory: true,
            };

        case CashRegistersDetailsActionType.FETCH_VERSION_CHECK_SUMS_SUCCESS: {
            if (state.versionsHistory !== null) {
                const updatedVersions: VersionWithCheckSums[] = [...state.versionsHistory.versions];
                for (let i = 0; i < updatedVersions.length; i++) {
                    if (updatedVersions[i].id === action.payload.versionId) {
                        updatedVersions[i].checksumHistory = action.payload.checkSumsHistory;
                        break;
                    }
                }
                return {
                    ...state,
                    versionsHistory: {
                        count: state.versionsHistory.count,
                        versions: updatedVersions,
                    },
                    isLoadingVersionsHistory: false,
                };
            } else return state;
        }

        case CashRegistersDetailsActionType.CHANGE_VERSION_VIEW: {
            const versions = [...state.versionsHistory.versions];
            const count = state.versionsHistory.count;
            const itemIndex = versions.findIndex((item) => item.id === action.payload.versionId);
            if (itemIndex !== -1) {
                versions[itemIndex].isExpanded = action.payload.isExpanded;
                if (!action.payload.isExpanded) versions[itemIndex].checksumHistory = null;
            }
            return {
                ...state,
                versionsHistory: { count, versions },
            };
        }

        case CashRegistersDetailsActionType.COLLAPSE_ALL_VERSIONS_VIEWS: {
            const versions = [...state.versionsHistory.versions];
            const count = state.versionsHistory.count;
            for (let i = 0; i < versions.length; i++) {
                versions[i].isExpanded = false;
                versions[i].checksumHistory = null;
            }
            return {
                ...state,
                versionsHistory: { count, versions },
            };
        }

        case CashRegistersDetailsActionType.ACTIVATE_MODEL_SUCCESS:
        case CashRegistersDetailsActionType.ELIMINATE_MODEL_SUCCESS:
            return {
                ...state,
                isLoading: true, //for details data reloading
            };

        case CashRegistersDetailsActionType.ACTIVATE_VERSION_SUCCESS:
        case CashRegistersDetailsActionType.MARK_VERSION_OUTDATED_SUCCESS:
            return {
                ...state,
                isLoadingVersionsHistory: true, //for versions list reloading
            };

        case CashRegistersDetailsActionType.FETCH_FACTORY_NUM_START:
            return {
                ...state,
                isLoadingFactoryNumbersList: true,
            };

        case CashRegistersDetailsActionType.FETCH_FACTORY_NUM_SUCCESS:
            return {
                ...state,
                isLoadingFactoryNumbersList: false,
                factoryNumbersList: action.payload,
            };

        case CashRegistersDetailsActionType.CHANGE_FACTORY_NUM_PAGING:
            return {
                ...state,
                isLoadingFactoryNumbersList: true,
                factoryNumbersListPageParams: action.payload,
            };

        case CashRegistersDetailsActionType.CHANGE_FACTORY_NUM_FILTER:
            return {
                ...state,
                isLoadingFactoryNumbersList: true,
                factoryNumbersListFilterParams: action.payload,
                factoryNumbersListPageParams: { ...state.factoryNumbersListPageParams, offset: 0 },
            };

        case CashRegistersDetailsActionType.FETCH_CASH_REGISTER_CARD_DATA_ERROR:
            return {
                ...state,
                isLoading: false,
                isLoadingStartAttestation: false,
                isLoadingCompleteAttestation: false,
                isLoadingVersionsHistory: false,
                isLoadingFactoryNumbersList: false,
                isLoadingDocumentsList: false,
                factoryNumbersList: {
                    count: 0,
                    cashRegisters: [],
                },
            };

        case CashRegistersDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_CARD_PAGE:
            return {
                ...state,
                cashRegisterModel: null,
                versionsHistory: null,
                currentInfoTab: 0,
            };

        case CashRegistersDetailsActionType.CHANGE_INFO_TAB:
            return {
                ...state,
                currentInfoTab: action.payload,
            };

        default:
            break;
    }

    return state;
}
